import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  LineChart, Line, Legend, Cell,
} from "recharts";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { useConfirm } from "../context/ConfirmContext";
import api, { getErrorMessage } from "../api";
import { requiredTrimmed } from "../utils/validate";
import { AsyncButton } from "../components/AsyncButton";
import { LoadingOverlay } from "../components/LoadingOverlay";
import { DateInput } from "../components/DateInput";
import { formatDate } from "../utils/dateFormat";
import { useRouteTab } from "../hooks/useRouteTab";
import { useTenantTimezone } from "../hooks/useTenantTimezone";

const NAVY = "#1e3a8a";
const TEAL = "#00c9a7";
const API_URL = process.env.REACT_APP_API_URL || "http://localhost:30801";

// ── API ───────────────────────────────────────────────────────────────────────

const P = {
  getCycles:          () => api.get("/api/performance/cycles").then(r => r.data),
  createCycle:        (d: any) => api.post("/api/performance/cycles", d).then(r => r.data),
  updateCycle:        (id: number, d: any) => api.patch(`/api/performance/cycles/${id}`, d).then(r => r.data),
  deleteCycle:        (id: number) => api.delete(`/api/performance/cycles/${id}`).then(r => r.data),
  activateCycle:      (id: number) => api.post(`/api/performance/cycles/${id}/activate`).then(r => r.data),
  cycleProgress:      (id: number) => api.get(`/api/performance/cycles/${id}/progress`).then(r => r.data),
  calibrationView:    (id: number) => api.get(`/api/performance/cycles/${id}/calibration`).then(r => r.data),
  sendReminders:      (id: number) => api.post(`/api/performance/cycles/${id}/remind`).then(r => r.data),
  getReviews:         (cycle_id?: number) => api.get("/api/performance/reviews", { params: { cycle_id } }).then(r => r.data),
  getMyReviews:       () => api.get("/api/performance/reviews/my").then(r => r.data),
  getToAction:        () => api.get("/api/performance/reviews/to-review").then(r => r.data),
  submitSelf:         (id: number, d: any) => api.patch(`/api/performance/reviews/${id}/self`, d).then(r => r.data),
  submitManager:      (id: number, d: any) => api.patch(`/api/performance/reviews/${id}/manager`, d).then(r => r.data),
  acknowledge:        (id: number) => api.patch(`/api/performance/reviews/${id}/acknowledge`, {}).then(r => r.data),
  calibrate:          (id: number, d: any) => api.patch(`/api/performance/reviews/${id}/calibrate`, d).then(r => r.data),
  updateDevPlan:      (id: number, d: any) => api.patch(`/api/performance/reviews/${id}/development-plan`, d).then(r => r.data),
  updatePromotion:    (id: number, d: any) => api.patch(`/api/performance/reviews/${id}/promotion`, d).then(r => r.data),
  reassignReviewer:   (id: number, d: any) => api.patch(`/api/performance/reviews/${id}/reassign-reviewer`, d).then(r => r.data),
  getComments:        (id: number) => api.get(`/api/performance/reviews/${id}/comments`).then(r => r.data),
  postComment:        (id: number, d: any) => api.post(`/api/performance/reviews/${id}/comments`, d).then(r => r.data),
  getPeerFeedbacks:   (cycle_id?: number, reviewee_id?: number) => api.get("/api/performance/peer-feedbacks", { params: { cycle_id, reviewee_id } }).then(r => r.data),
  submitPeerFeedback: (d: any) => api.post("/api/performance/peer-feedbacks", d).then(r => r.data),
  getMgrFeedbacks:    (cycle_id?: number, reviewee_id?: number) => api.get("/api/performance/manager-feedbacks", { params: { cycle_id, reviewee_id } }).then(r => r.data),
  submitMgrFeedback:  (d: any) => api.post("/api/performance/manager-feedbacks", d).then(r => r.data),
  getAnalytics:       () => api.get("/api/performance/analytics").then(r => r.data),
  // Goals
  getMyGoals:         () => api.get("/api/performance/goals/my").then(r => r.data),
  getTeamGoals:       () => api.get("/api/performance/goals/team").then(r => r.data),
  createGoal:         (d: any) => api.post("/api/performance/goals", d).then(r => r.data),
  updateGoal:         (id: number, d: any) => api.patch(`/api/performance/goals/${id}`, d).then(r => r.data),
  deleteGoal:         (id: number) => api.delete(`/api/performance/goals/${id}`).then(r => r.data),
  // Check-ins
  getCheckins:        () => api.get("/api/performance/checkins").then(r => r.data),
  createCheckin:      (d: any) => api.post("/api/performance/checkins", d).then(r => r.data),
  updateCheckin:      (id: number, d: any) => api.patch(`/api/performance/checkins/${id}`, d).then(r => r.data),
  deleteCheckin:      (id: number) => api.delete(`/api/performance/checkins/${id}`).then(r => r.data),
  // Anytime Feedback
  getAnytimeFeedback: () => api.get("/api/performance/anytime-feedback").then(r => r.data),
  getGivenFeedback:   () => api.get("/api/performance/anytime-feedback/given").then(r => r.data),
  giveAnytimeFeedback:(d: any) => api.post("/api/performance/anytime-feedback", d).then(r => r.data),
};

// ── Shared styles ──────────────────────────────────────────────────────────────

const inp: React.CSSProperties = {
  width: "100%", padding: "9px 12px", borderRadius: 8, fontSize: 13,
  border: "1.5px solid #e2e8f0", outline: "none", color: "#1e293b", boxSizing: "border-box",
};
const lbl: React.CSSProperties = { display: "block", fontSize: 11, fontWeight: 600, color: "#475569", marginBottom: 4 };
const card: React.CSSProperties = { background: "white", border: "1px solid #e2e8f0", borderRadius: 12, padding: 20 };
const primaryBtn: React.CSSProperties = {
  background: `linear-gradient(135deg,${NAVY},${TEAL})`, color: "white", border: "none",
  padding: "10px 22px", borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: "pointer",
};
const outlineBtn: React.CSSProperties = {
  background: "white", color: NAVY, border: `1.5px solid ${NAVY}`,
  padding: "8px 16px", borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: "pointer",
};

// ── Constants ─────────────────────────────────────────────────────────────────

const REVIEW_TYPES = ["annual", "quarterly", "mid_year", "probation"];
const RATING_LABELS: Record<number, { label: string; color: string }> = {
  1: { label: "Needs Improvement",    color: "#ef4444" },
  2: { label: "Below Expectations",   color: "#f59e0b" },
  3: { label: "Meets Expectations",   color: "#3b82f6" },
  4: { label: "Exceeds Expectations", color: "#10b981" },
  5: { label: "Outstanding",          color: "#8b5cf6" },
};
const GOAL_STATUS_COLORS: Record<string, string> = {
  pending: "#94a3b8", in_progress: "#3b82f6", met: "#16a34a", partial: "#d97706", missed: "#ef4444",
};
const STATUS_CYCLE: Record<string, { label: string; color: string; bg: string }> = {
  draft:  { label: "Draft",  color: "#64748b", bg: "#f1f5f9" },
  active: { label: "Active", color: "#16a34a", bg: "#dcfce7" },
  closed: { label: "Closed", color: "#94a3b8", bg: "#f8fafc" },
};
const STATUS_REVIEW: Record<string, { label: string; color: string; bg: string }> = {
  pending:      { label: "Pending",       color: "#d97706", bg: "#fef9c3" },
  self_done:    { label: "Self Done",      color: "#2563eb", bg: "#dbeafe" },
  manager_done: { label: "Manager Done",   color: "#7c3aed", bg: "#ede9fe" },
  completed:    { label: "Completed",      color: "#16a34a", bg: "#dcfce7" },
  acknowledged: { label: "Acknowledged",   color: "#0f766e", bg: "#ccfbf1" },
};
const PROMO_OPTIONS = [
  { value: "none", label: "No recommendation" },
  { value: "promotion", label: "Promotion recommended" },
  { value: "increment", label: "Salary increment recommended" },
  { value: "both", label: "Promotion + increment" },
];

// ── Star Rating ───────────────────────────────────────────────────────────────

function StarRating({ value, onChange, readonly, size = 26 }: {
  value: number; onChange?: (v: number) => void; readonly?: boolean; size?: number;
}) {
  const [hover, setHover] = useState(0);
  return (
    <div style={{ display: "flex", gap: 3, alignItems: "center" }}>
      {[1,2,3,4,5].map(n => (
        <span key={n} onClick={() => !readonly && onChange?.(n)}
          onMouseEnter={() => !readonly && setHover(n)}
          onMouseLeave={() => !readonly && setHover(0)}
          style={{ fontSize: size, cursor: readonly ? "default" : "pointer",
            color: n <= (hover || value) ? "#f59e0b" : "#e2e8f0", transition: "color 0.1s" }}>★</span>
      ))}
      {value > 0 && (
        <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 700, color: RATING_LABELS[Math.round(value)]?.color || "#64748b" }}>
          {RATING_LABELS[Math.round(value)]?.label} ({value.toFixed(1)})
        </span>
      )}
    </div>
  );
}

// ── Progress Bar ──────────────────────────────────────────────────────────────

function ProgressBar({ pct, color = TEAL, height = 8 }: { pct: number; color?: string; height?: number }) {
  return (
    <div style={{ background: "#f1f5f9", borderRadius: height, height, overflow: "hidden" }}>
      <div style={{ background: color, height: "100%", width: `${Math.min(100, pct)}%`, borderRadius: height, transition: "width 0.4s" }} />
    </div>
  );
}

// ── Rating Categories Editor ──────────────────────────────────────────────────

function RatingCategoriesEditor({ cats, onChange, field, readonly }: {
  cats: any[]; onChange?: (c: any[]) => void; field: "self_rating" | "manager_rating"; readonly?: boolean;
}) {
  if (!cats?.length) return null;
  return (
    <div style={{ display: "grid", gap: 12 }}>
      {cats.map((c, i) => (
        <div key={i}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#475569", marginBottom: 4 }}>
            {c.name} <span style={{ color: "#94a3b8", fontWeight: 400 }}>({c.weight}%)</span>
          </div>
          <StarRating value={c[field] || 0} size={22} readonly={readonly}
            onChange={v => {
              if (!onChange) return;
              onChange(cats.map((x, j) => j === i ? { ...x, [field]: v } : x));
            }} />
        </div>
      ))}
    </div>
  );
}

// ── Goals Editor (in review modal) ───────────────────────────────────────────

function GoalsEditor({ goals, onChange, readonly, showManagerNotes = false }: {
  goals: any[]; onChange?: (g: any[]) => void; readonly?: boolean; showManagerNotes?: boolean;
}) {
  return (
    <div>
      {goals.map((g, i) => (
        <div key={i} style={{ border: "1px solid #f1f5f9", borderRadius: 8, padding: "10px 12px", marginBottom: 8, background: g.carried_forward ? "#fffbeb" : "white" }}>
          <div style={{ display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 6 }}>
            <div style={{ flex: 1 }}>
              {readonly
                ? <div style={{ fontSize: 13, fontWeight: 500, color: "#1e293b" }}>
                    {g.carried_forward && <span style={{ fontSize: 10, background: "#fef9c3", color: "#854d0e", borderRadius: 8, padding: "1px 6px", marginRight: 6, border: "1px solid #fde047" }}>↩ Carried</span>}
                    {g.title}
                  </div>
                : <input value={g.title || ""} placeholder="Goal title" style={{ ...inp, marginBottom: 4 }}
                    onChange={e => { const ng = [...goals]; ng[i] = {...ng[i], title: e.target.value}; onChange?.(ng); }} />
              }
              {g.description && <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>{g.description}</div>}
            </div>
            <select value={g.status || "pending"} disabled={readonly}
              onChange={e => { const ng = [...goals]; ng[i] = {...ng[i], status: e.target.value}; onChange?.(ng); }}
              style={{ ...inp, width: 105, flexShrink: 0, fontSize: 12, fontWeight: 600, color: GOAL_STATUS_COLORS[g.status] || "#64748b", cursor: readonly ? "default" : "pointer" }}>
              {Object.keys(GOAL_STATUS_COLORS).map(s => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
            </select>
            {!readonly && (
              <button onClick={() => onChange?.(goals.filter((_, j) => j !== i))}
                style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8", fontSize: 18, padding: "3px 5px" }}>×</button>
            )}
          </div>
          {/* Due date + progress */}
          <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: showManagerNotes ? 6 : 0 }}>
            {!readonly ? (
              <>
                <div style={{ flex: 1 }}>
                  <label style={{ ...lbl, fontSize: 10 }}>Due date</label>
                  <DateInput value={g.due_date || ""} style={{ ...inp, fontSize: 12 }}
                    onChange={e => { const ng = [...goals]; ng[i] = {...ng[i], due_date: e.target.value}; onChange?.(ng); }} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ ...lbl, fontSize: 10 }}>Progress: {g.progress_pct || 0}%</label>
                  <input type="range" min={0} max={100} value={g.progress_pct || 0} style={{ width: "100%" }}
                    onChange={e => { const ng = [...goals]; ng[i] = {...ng[i], progress_pct: parseInt(e.target.value)}; onChange?.(ng); }} />
                </div>
              </>
            ) : (
              <>
                {g.due_date && <span style={{ fontSize: 11, color: "#64748b" }}>Due: {g.due_date}</span>}
                {(g.progress_pct > 0) && (
                  <div style={{ flex: 1, minWidth: 80 }}>
                    <div style={{ fontSize: 11, color: "#64748b", marginBottom: 3 }}>{g.progress_pct}%</div>
                    <ProgressBar pct={g.progress_pct} color={GOAL_STATUS_COLORS[g.status] || TEAL} height={6} />
                  </div>
                )}
              </>
            )}
          </div>
          {showManagerNotes && (
            <div>
              <label style={{ ...lbl, fontSize: 10, color: "#94a3b8" }}>Manager note</label>
              {readonly
                ? g.manager_note && <div style={{ fontSize: 12, color: "#64748b", fontStyle: "italic" }}>{g.manager_note}</div>
                : <input value={g.manager_note || ""} placeholder="Add note…" style={{ ...inp, fontSize: 12 }}
                    onChange={e => { const ng = [...goals]; ng[i] = {...ng[i], manager_note: e.target.value}; onChange?.(ng); }} />
              }
            </div>
          )}
        </div>
      ))}
      {!readonly && (
        <button onClick={() => onChange?.([...goals, { title: "", status: "pending", progress_pct: 0 }])}
          style={{ fontSize: 12, color: NAVY, background: "none", border: `1px dashed ${NAVY}`, borderRadius: 6, padding: "6px 12px", cursor: "pointer" }}>
          + Add Goal
        </button>
      )}
    </div>
  );
}

// ── Development Plan Editor ───────────────────────────────────────────────────

function DevPlanEditor({ plan, onChange, readonly }: {
  plan: any[]; onChange?: (p: any[]) => void; readonly?: boolean;
}) {
  const blank = { action: "", target_date: "", status: "pending", owner: "employee" };
  return (
    <div>
      {plan.map((item, i) => (
        <div key={i} style={{ border: "1px solid #f1f5f9", borderRadius: 8, padding: "10px 12px", marginBottom: 8 }}>
          {readonly ? (
            <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
              <div style={{ flex: 1, fontSize: 13, color: "#1e293b" }}>{item.action}</div>
              {item.target_date && <span style={{ fontSize: 11, color: "#64748b" }}>{item.target_date}</span>}
              <span style={{ fontSize: 11, color: "#64748b", textTransform: "capitalize" }}>{item.owner}</span>
              <span style={{ fontSize: 11, fontWeight: 600, color: item.status === "done" ? "#16a34a" : item.status === "in_progress" ? "#2563eb" : "#94a3b8" }}>
                {item.status?.replace("_", " ")}
              </span>
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "1fr auto auto auto auto", gap: 8, alignItems: "center" }}>
              <input value={item.action || ""} placeholder="Action item" style={inp}
                onChange={e => { const np = [...plan]; np[i] = {...np[i], action: e.target.value}; onChange?.(np); }} />
              <DateInput value={item.target_date || ""} style={{ ...inp, width: 130 }}
                onChange={e => { const np = [...plan]; np[i] = {...np[i], target_date: e.target.value}; onChange?.(np); }} />
              <select value={item.owner || "employee"} style={{ ...inp, width: 100 }}
                onChange={e => { const np = [...plan]; np[i] = {...np[i], owner: e.target.value}; onChange?.(np); }}>
                <option value="employee">Employee</option>
                <option value="manager">Manager</option>
              </select>
              <select value={item.status || "pending"} style={{ ...inp, width: 100 }}
                onChange={e => { const np = [...plan]; np[i] = {...np[i], status: e.target.value}; onChange?.(np); }}>
                {["pending","in_progress","done"].map(s => <option key={s} value={s}>{s.replace("_"," ")}</option>)}
              </select>
              <button onClick={() => onChange?.(plan.filter((_,j) => j !== i))}
                style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8", fontSize: 18 }}>×</button>
            </div>
          )}
        </div>
      ))}
      {!readonly && (
        <button onClick={() => onChange?.([...plan, { ...blank }])}
          style={{ fontSize: 12, color: TEAL, background: "none", border: `1px dashed ${TEAL}`, borderRadius: 6, padding: "6px 12px", cursor: "pointer" }}>
          + Add Action Item
        </button>
      )}
    </div>
  );
}

// ── Discussion Thread ─────────────────────────────────────────────────────────

function DiscussionThread({ reviewId, userId }: { reviewId: number; userId: number }) {
  const tz = useTenantTimezone();
  const qc = useQueryClient();
  const { toast } = useToast();
  const [msg, setMsg] = useState("");
  const { data: comments = [] } = useQuery({
    queryKey: ["review-comments", reviewId],
    queryFn: () => P.getComments(reviewId),
  });
  const postMut = useMutation({
    mutationFn: () => P.postComment(reviewId, { message: msg }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["review-comments", reviewId] }); setMsg(""); },
    onError: (e: any) => toast(e.response?.data?.detail || "Failed to post", "error"),
  });
  return (
    <div>
      <LoadingOverlay active={postMut.isPending} />
      <div style={{ display: "grid", gap: 8, marginBottom: 12 }}>
        {(comments as any[]).length === 0 && (
          <p style={{ color: "#94a3b8", fontSize: 13, margin: 0 }}>No comments yet. Start the discussion.</p>
        )}
        {(comments as any[]).map((c: any) => (
          <div key={c.id} style={{
            background: c.user_id === userId ? "#eff6ff" : "#f8fafc",
            borderRadius: 10, padding: "10px 14px",
            borderLeft: `3px solid ${c.user_id === userId ? NAVY : "#e2e8f0"}`,
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: NAVY }}>{c.author_name}</span>
              <span style={{ fontSize: 11, color: "#94a3b8" }}>{formatDate(c.created_at, "short", tz)}</span>
            </div>
            <div style={{ fontSize: 13, color: "#1e293b" }}>{c.message}</div>
          </div>
        ))}
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <textarea value={msg} onChange={e => setMsg(e.target.value)} rows={2} placeholder="Write a comment…"
          style={{ ...inp, flex: 1, resize: "none" }}
          onKeyDown={e => { if (e.key === "Enter" && e.ctrlKey && msg.trim()) postMut.mutate(); }} />
        <button onClick={() => postMut.mutate()} disabled={!msg.trim() || postMut.isPending} style={{ ...primaryBtn, alignSelf: "flex-end" }}>
          {postMut.isPending ? "…" : "Post"}
        </button>
      </div>
      <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>Ctrl+Enter to submit</div>
    </div>
  );
}

// ── Review Modal ──────────────────────────────────────────────────────────────

function ReviewModal({ review: init, userId, userRole, employees, onClose }: {
  review: any; userId: number; userRole: string; employees: any[]; onClose: () => void;
}) {
  const tz = useTenantTimezone();
  const qc = useQueryClient();
  const { toast } = useToast();
  const [rev, setRev] = useState(init);
  const isAdmin = ["admin", "admin_assistant", "hr"].includes(userRole);
  const isSelf = rev.employee_id === userId;
  const isReviewer = rev.reviewer_id === userId;

  const [selfForm, setSelfForm] = useState({ self_rating: rev.self_rating || 0, self_comments: rev.self_comments || "", goals: rev.goals || [], rating_categories: rev.rating_categories || [] });
  const [mgrForm, setMgrForm] = useState({ manager_rating: rev.manager_rating || 0, manager_comments: rev.manager_comments || "", goals: rev.goals || [], rating_categories: rev.rating_categories || [], development_plan: rev.development_plan || [], promotion_recommendation: rev.promotion_recommendation || "none", increment_pct: rev.increment_pct || "", promotion_notes: rev.promotion_notes || "" });
  const [hrForm, setHrForm] = useState({ hr_final_rating: rev.hr_final_rating || 0, hr_notes: rev.hr_notes || "" });
  const [devPlan, setDevPlan] = useState(rev.development_plan || []);
  const [showReassign, setShowReassign] = useState(false);
  const [newReviewerId, setNewReviewerId] = useState<string>(rev.reviewer_id?.toString() || "");
  const [peerForm, setPeerForm] = useState({ rating: 0, comments: "" });
  const [mgrFeedbackForm, setMgrFeedbackForm] = useState({ rating: 0, comments: "" });
  const [activeSection, setActiveSection] = useState<"review" | "devplan" | "discussion" | "peer" | "upward">("review");
  const [pdfBusy, setPdfBusy] = useState(false);

  const inv = () => { qc.invalidateQueries({ queryKey: ["my-reviews"] }); qc.invalidateQueries({ queryKey: ["reviews-to-action"] }); qc.invalidateQueries({ queryKey: ["cycle-reviews"] }); };

  const selfMut = useMutation({ mutationFn: () => P.submitSelf(rev.id, selfForm), onSuccess: d => { setRev(d); inv(); toast("Self-assessment saved"); }, onError: (e: any) => toast(e.response?.data?.detail || "Failed", "error") });
  const mgrMut = useMutation({ mutationFn: () => P.submitManager(rev.id, { ...mgrForm, final_rating: mgrForm.manager_rating }), onSuccess: d => { setRev(d); inv(); toast("Manager review submitted"); }, onError: (e: any) => toast(e.response?.data?.detail || "Failed", "error") });
  const ackMut = useMutation({ mutationFn: () => P.acknowledge(rev.id), onSuccess: d => { setRev(d); inv(); toast("Review acknowledged"); }, onError: (e: any) => toast(e.response?.data?.detail || "Failed", "error") });
  const calMut = useMutation({ mutationFn: () => P.calibrate(rev.id, { hr_final_rating: hrForm.hr_final_rating || null, hr_notes: hrForm.hr_notes }), onSuccess: d => { setRev(d); inv(); toast("Calibration saved"); }, onError: () => toast("Failed", "error") });
  const devMut = useMutation({ mutationFn: () => P.updateDevPlan(rev.id, { development_plan: devPlan }), onSuccess: d => { setRev(d); inv(); toast("Plan saved"); }, onError: () => toast("Failed", "error") });
  const reassignMut = useMutation({ mutationFn: () => P.reassignReviewer(rev.id, { reviewer_id: newReviewerId ? parseInt(newReviewerId) : null }), onSuccess: d => { setRev(d); inv(); setShowReassign(false); toast("Reviewer updated"); }, onError: () => toast("Failed", "error") });
  const peerMut = useMutation({ mutationFn: () => P.submitPeerFeedback({ cycle_id: rev.cycle_id, reviewee_id: rev.employee_id, ...peerForm }), onSuccess: () => toast("Peer feedback submitted"), onError: (e: any) => toast(e.response?.data?.detail || "Failed", "error") });
  const mgrFbMut = useMutation({ mutationFn: () => P.submitMgrFeedback({ cycle_id: rev.cycle_id, reviewee_id: rev.reviewer_id, ...mgrFeedbackForm }), onSuccess: () => toast("Feedback on manager submitted"), onError: (e: any) => toast(e.response?.data?.detail || "Failed", "error") });

  async function handlePdf() {
    setPdfBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/performance/reviews/${rev.id}/pdf`, { credentials: "include" });
      if (!r.ok) { toast("PDF failed", "error"); return; }
      const b = await r.blob();
      const u = URL.createObjectURL(b);
      const a = document.createElement("a"); a.href = u; a.download = `review_${(rev.employee_name || "").replace(/ /g,"_")}.pdf`; a.click();
      URL.revokeObjectURL(u);
    } catch { toast("PDF failed", "error"); }
    finally { setPdfBusy(false); }
  }

  const st = STATUS_REVIEW[rev.status] || STATUS_REVIEW.pending;
  const hasCats = (rev.rating_categories || []).length > 0;
  const isDone = ["completed","acknowledged"].includes(rev.status);

  const SECTIONS = [
    { key: "review", label: "Review" },
    ...(isDone ? [{ key: "devplan", label: "Dev Plan" }] : []),
    ...(isDone ? [{ key: "discussion", label: "Discussion" }] : []),
    ...(rev.cycle_peer_feedback_enabled && !isSelf ? [{ key: "peer", label: "Peer Feedback" }] : []),
    ...(rev.cycle_upward_feedback_enabled && isSelf && rev.reviewer_id ? [{ key: "upward", label: "Manager Feedback" }] : []),
  ];

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }} onClick={onClose}>
      <LoadingOverlay active={
        selfMut.isPending || mgrMut.isPending || ackMut.isPending || calMut.isPending ||
        devMut.isPending || reassignMut.isPending || peerMut.isPending || mgrFbMut.isPending ||
        pdfBusy
      } />
      <div className="modal-sheet" style={{ background: "white", borderRadius: 16, padding: 0, maxWidth: 700, width: "100%", maxHeight: "92vh", overflow: "hidden", display: "flex", flexDirection: "column" }} onClick={e => e.stopPropagation()}>

        {/* Modal header */}
        <div style={{ padding: "18px 24px 0", borderBottom: "1px solid #f1f5f9" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
            <div>
              <h2 style={{ margin: 0, color: NAVY, fontSize: 16 }}>{rev.cycle_title}</h2>
              <p style={{ margin: "3px 0 0", fontSize: 12, color: "#64748b" }}>
                {rev.employee_name} · {rev.employee_designation || rev.employee_department || ""}
                {rev.reviewer_name ? ` · Reviewer: ${rev.reviewer_name}` : ""}
              </p>
            </div>
            <div style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0 }}>
              <span style={{ background: st.bg, color: st.color, padding: "2px 9px", borderRadius: 20, fontSize: 11, fontWeight: 700 }}>{st.label}</span>
              {isDone && <AsyncButton onClick={handlePdf} style={{ ...outlineBtn, fontSize: 11, padding: "4px 10px" }}>↓ PDF</AsyncButton>}
              {isAdmin && <button onClick={() => setShowReassign(s => !s)} style={{ ...outlineBtn, fontSize: 11, padding: "4px 10px" }}>Reassign</button>}
              <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 20, color: "#94a3b8" }}>×</button>
            </div>
          </div>
          {/* Section tabs */}
          <div style={{ display: "flex", gap: 0 }}>
            {SECTIONS.map(s => (
              <button key={s.key} onClick={() => setActiveSection(s.key as any)} style={{
                padding: "7px 16px", border: "none", background: "none", cursor: "pointer", fontSize: 13, fontWeight: 600,
                color: activeSection === s.key ? NAVY : "#94a3b8",
                borderBottom: activeSection === s.key ? `2px solid ${NAVY}` : "2px solid transparent",
                marginBottom: -1,
              }}>{s.label}</button>
            ))}
          </div>
        </div>

        {/* Modal body */}
        <div style={{ overflowY: "auto", padding: "20px 24px", flex: 1 }}>

          {/* Reassign */}
          {showReassign && isAdmin && (
            <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: 14, marginBottom: 16 }}>
              <label style={lbl}>Assign Reviewer</label>
              <div style={{ display: "flex", gap: 8 }}>
                <select value={newReviewerId} onChange={e => setNewReviewerId(e.target.value)} style={{ ...inp, flex: 1 }}>
                  <option value="">— No reviewer —</option>
                  {employees.filter(e => e.id !== rev.employee_id).map((e: any) => (
                    <option key={e.id} value={e.id}>{e.name} ({e.role})</option>
                  ))}
                </select>
                <button onClick={() => reassignMut.mutate()} disabled={reassignMut.isPending} style={primaryBtn}>Save</button>
              </div>
            </div>
          )}

          {/* ── REVIEW section ── */}
          {activeSection === "review" && (
            <div>
              {/* Category breakdown (read-only if already rated) */}
              {hasCats && rev.status !== "pending" && (
                <div style={{ background: "#f8fafc", borderRadius: 10, padding: 14, marginBottom: 20 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#475569", marginBottom: 10 }}>Rating Breakdown</div>
                  <div style={{ display: "grid", gap: 8 }}>
                    {(rev.rating_categories || []).map((c: any, i: number) => (
                      <div key={i} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <span style={{ fontSize: 12, color: "#64748b", width: 130, flexShrink: 0 }}>{c.name} <span style={{ color: "#94a3b8" }}>({c.weight}%)</span></span>
                        <div style={{ flex: 1, display: "flex", gap: 16 }}>
                          {c.self_rating != null && <span style={{ fontSize: 12 }}>Self: <strong style={{ color: "#f59e0b" }}>{"★".repeat(Math.round(c.self_rating))} {c.self_rating?.toFixed(1)}</strong></span>}
                          {c.manager_rating != null && <span style={{ fontSize: 12 }}>Mgr: <strong style={{ color: "#2563eb" }}>{"★".repeat(Math.round(c.manager_rating))} {c.manager_rating?.toFixed(1)}</strong></span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Goals */}
              <div style={{ marginBottom: 20 }}>
                <h3 style={{ margin: "0 0 10px", fontSize: 14, color: "#475569" }}>Goals</h3>
                <GoalsEditor
                  goals={isSelf && rev.status === "pending" ? selfForm.goals : (isReviewer || isAdmin) && rev.status === "self_done" ? mgrForm.goals : (rev.goals || [])}
                  onChange={isSelf && rev.status === "pending" ? g => setSelfForm(f => ({...f, goals: g})) : (isReviewer || isAdmin) && rev.status === "self_done" ? g => setMgrForm(f => ({...f, goals: g})) : undefined}
                  readonly={!(isSelf && rev.status === "pending") && !((isReviewer || isAdmin) && rev.status === "self_done")}
                  showManagerNotes={(isReviewer || isAdmin) && rev.status === "self_done"}
                />
              </div>

              {/* Self-assessment */}
              <div style={{ borderTop: "1px solid #f1f5f9", paddingTop: 18, marginBottom: 18 }}>
                <h3 style={{ margin: "0 0 12px", fontSize: 14, color: "#475569" }}>Self Assessment</h3>
                {isSelf && rev.status === "pending" ? (
                  <>
                    {hasCats && <div style={{ marginBottom: 14 }}><label style={lbl}>Rate by category</label><RatingCategoriesEditor cats={selfForm.rating_categories} field="self_rating" onChange={c => setSelfForm(f => ({...f, rating_categories: c}))} /></div>}
                    <div style={{ marginBottom: 10 }}><label style={lbl}>Overall Self Rating</label><StarRating value={selfForm.self_rating} onChange={v => setSelfForm(f => ({...f, self_rating: v}))} /></div>
                    <div style={{ marginBottom: 14 }}><label style={lbl}>Comments</label>
                      <textarea value={selfForm.self_comments} rows={3} onChange={e => setSelfForm(f => ({...f, self_comments: e.target.value}))} placeholder="Describe your achievements, challenges, growth…" style={{ ...inp, resize: "vertical" }} /></div>
                    <button onClick={() => selfMut.mutate()} disabled={!selfForm.self_rating || selfMut.isPending} style={primaryBtn}>{selfMut.isPending ? "Saving…" : "Submit Self-Assessment"}</button>
                  </>
                ) : (
                  <>
                    <StarRating value={rev.self_rating || 0} readonly />
                    {rev.self_comments && <p style={{ margin: "8px 0 0", fontSize: 13, color: "#475569", background: "#f8fafc", borderRadius: 8, padding: 12 }}>{rev.self_comments}</p>}
                    {!rev.self_rating && <p style={{ color: "#94a3b8", fontSize: 13, margin: "6px 0 0" }}>Not yet submitted.</p>}
                  </>
                )}
              </div>

              {/* Manager assessment */}
              {(isReviewer || isAdmin || rev.manager_rating) && (
                <div style={{ borderTop: "1px solid #f1f5f9", paddingTop: 18, marginBottom: 18 }}>
                  <h3 style={{ margin: "0 0 12px", fontSize: 14, color: "#475569" }}>Manager Review</h3>
                  {(isReviewer || isAdmin) && rev.status === "self_done" ? (
                    <>
                      {hasCats && <div style={{ marginBottom: 14 }}><label style={lbl}>Rate by category</label><RatingCategoriesEditor cats={mgrForm.rating_categories} field="manager_rating" onChange={c => setMgrForm(f => ({...f, rating_categories: c}))} /></div>}
                      <div style={{ marginBottom: 10 }}><label style={lbl}>Overall Rating</label><StarRating value={mgrForm.manager_rating} onChange={v => setMgrForm(f => ({...f, manager_rating: v}))} /></div>
                      <div style={{ marginBottom: 14 }}><label style={lbl}>Comments</label>
                        <textarea value={mgrForm.manager_comments} rows={3} onChange={e => setMgrForm(f => ({...f, manager_comments: e.target.value}))} placeholder="Strengths, areas for improvement…" style={{ ...inp, resize: "vertical" }} /></div>
                      <div style={{ marginBottom: 14 }}><label style={lbl}>Development Plan</label><DevPlanEditor plan={mgrForm.development_plan} onChange={p => setMgrForm(f => ({...f, development_plan: p}))} /></div>
                      <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: 14, marginBottom: 14 }}>
                        <label style={lbl}>Recommendation</label>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 8 }}>
                          <div><label style={lbl}>Type</label>
                            <select value={mgrForm.promotion_recommendation} onChange={e => setMgrForm(f => ({...f, promotion_recommendation: e.target.value}))} style={inp}>
                              {PROMO_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select></div>
                          {["increment","both"].includes(mgrForm.promotion_recommendation) && (
                            <div><label style={lbl}>Increment %</label><input type="number" value={mgrForm.increment_pct} placeholder="10" onChange={e => setMgrForm(f => ({...f, increment_pct: e.target.value}))} style={inp} /></div>
                          )}
                        </div>
                        {mgrForm.promotion_recommendation !== "none" && (
                          <div><label style={lbl}>Notes</label><textarea value={mgrForm.promotion_notes} rows={2} onChange={e => setMgrForm(f => ({...f, promotion_notes: e.target.value}))} style={{ ...inp, resize: "vertical" }} /></div>
                        )}
                      </div>
                      <button onClick={() => mgrMut.mutate()} disabled={!mgrForm.manager_rating || mgrMut.isPending} style={primaryBtn}>{mgrMut.isPending ? "Submitting…" : "Submit Manager Review"}</button>
                    </>
                  ) : (
                    <>
                      <StarRating value={rev.manager_rating || 0} readonly />
                      {rev.manager_comments && <p style={{ margin: "8px 0 0", fontSize: 13, color: "#475569", background: "#f8fafc", borderRadius: 8, padding: 12 }}>{rev.manager_comments}</p>}
                      {!rev.manager_rating && <p style={{ color: "#94a3b8", fontSize: 13, margin: "6px 0 0" }}>{rev.status === "pending" ? "Waiting for self-assessment first." : "Awaiting manager review."}</p>}
                    </>
                  )}
                </div>
              )}

              {/* Final + acknowledge */}
              {(rev.effective_final_rating > 0) && (
                <div style={{ textAlign: "center", padding: "16px 0", borderTop: "1px solid #f1f5f9" }}>
                  <div style={{ fontSize: 12, color: "#64748b", marginBottom: 6 }}>{rev.hr_final_rating ? "HR Calibrated Final Rating" : "Final Rating"}</div>
                  <StarRating value={rev.effective_final_rating} readonly size={32} />
                </div>
              )}
              {isSelf && rev.status === "completed" && (
                <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 10, padding: 14, marginTop: 14 }}>
                  <p style={{ margin: "0 0 10px", fontSize: 13, color: "#92400e" }}>Your manager has completed this review. Please acknowledge that you've read it.</p>
                  <button onClick={() => ackMut.mutate()} disabled={ackMut.isPending} style={{ ...primaryBtn, background: "#d97706" }}>{ackMut.isPending ? "Acknowledging…" : "Acknowledge Review"}</button>
                </div>
              )}
              {rev.acknowledged_at && <div style={{ fontSize: 11, color: "#94a3b8", textAlign: "center", marginTop: 12 }}>Acknowledged on {formatDate(rev.acknowledged_at, "short", tz)}</div>}

              {/* Promotion badge */}
              {rev.promotion_recommendation && rev.promotion_recommendation !== "none" && (
                <div style={{ background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 10, padding: 12, marginTop: 14 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#166534" }}>{PROMO_OPTIONS.find(o => o.value === rev.promotion_recommendation)?.label}</div>
                  {rev.increment_pct && <div style={{ fontSize: 12, color: "#166534", marginTop: 3 }}>Increment: {rev.increment_pct}%</div>}
                  {rev.promotion_notes && <div style={{ fontSize: 12, color: "#475569", marginTop: 3 }}>{rev.promotion_notes}</div>}
                </div>
              )}

              {/* HR calibration */}
              {isAdmin && isDone && (
                <div style={{ borderTop: "1px solid #f1f5f9", paddingTop: 16, marginTop: 16 }}>
                  <h3 style={{ margin: "0 0 10px", fontSize: 14, color: "#475569" }}>HR Calibration</h3>
                  <div style={{ display: "grid", gap: 10 }}>
                    <div><label style={lbl}>Override Final Rating</label><StarRating value={hrForm.hr_final_rating} onChange={v => setHrForm(f => ({...f, hr_final_rating: v}))} /></div>
                    <div><label style={lbl}>HR Notes (internal)</label><textarea value={hrForm.hr_notes} rows={2} onChange={e => setHrForm(f => ({...f, hr_notes: e.target.value}))} style={{ ...inp, resize: "vertical" }} /></div>
                    <button onClick={() => calMut.mutate()} disabled={calMut.isPending} style={{ ...outlineBtn, width: "fit-content" }}>{calMut.isPending ? "Saving…" : "Save Calibration"}</button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── DEV PLAN section ── */}
          {activeSection === "devplan" && (
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <h3 style={{ margin: 0, fontSize: 14, color: "#475569" }}>Development Plan</h3>
                {(isReviewer || isAdmin) && <button onClick={() => devMut.mutate()} disabled={devMut.isPending} style={{ ...outlineBtn, fontSize: 12, padding: "5px 12px" }}>{devMut.isPending ? "Saving…" : "Save Plan"}</button>}
              </div>
              <DevPlanEditor plan={devPlan} onChange={(isReviewer || isAdmin) ? setDevPlan : undefined} readonly={!isReviewer && !isAdmin} />
              {devPlan.length === 0 && !isAdmin && !isReviewer && <p style={{ color: "#94a3b8", fontSize: 13 }}>No development plan yet.</p>}
            </div>
          )}

          {/* ── DISCUSSION section ── */}
          {activeSection === "discussion" && (
            <div>
              <h3 style={{ margin: "0 0 14px", fontSize: 14, color: "#475569" }}>Discussion Thread</h3>
              <DiscussionThread reviewId={rev.id} userId={userId} />
            </div>
          )}

          {/* ── PEER FEEDBACK section ── */}
          {activeSection === "peer" && !isSelf && (
            <div>
              <h3 style={{ margin: "0 0 14px", fontSize: 14, color: "#475569" }}>Give Peer Feedback to {rev.employee_name}</h3>
              <StarRating value={peerForm.rating} onChange={v => setPeerForm(f => ({...f, rating: v}))} />
              <textarea value={peerForm.comments} rows={3} placeholder="Your feedback on this colleague…" onChange={e => setPeerForm(f => ({...f, comments: e.target.value}))} style={{ ...inp, marginTop: 10, resize: "vertical" }} />
              <button onClick={() => peerMut.mutate()} disabled={peerMut.isPending} style={{ ...primaryBtn, marginTop: 10 }}>{peerMut.isPending ? "Submitting…" : "Submit Peer Feedback"}</button>
            </div>
          )}

          {/* ── UPWARD FEEDBACK section ── */}
          {activeSection === "upward" && isSelf && rev.reviewer_id && (
            <div>
              <h3 style={{ margin: "0 0 14px", fontSize: 14, color: "#475569" }}>Give Feedback on Your Manager ({rev.reviewer_name})</h3>
              <p style={{ fontSize: 13, color: "#64748b", margin: "0 0 14px" }}>Your feedback is anonymous to your manager. Only HR can see who submitted it.</p>
              <StarRating value={mgrFeedbackForm.rating} onChange={v => setMgrFeedbackForm(f => ({...f, rating: v}))} />
              <textarea value={mgrFeedbackForm.comments} rows={3} placeholder="Communication, support, clarity of direction…" onChange={e => setMgrFeedbackForm(f => ({...f, comments: e.target.value}))} style={{ ...inp, marginTop: 10, resize: "vertical" }} />
              <button onClick={() => mgrFbMut.mutate()} disabled={mgrFbMut.isPending} style={{ ...primaryBtn, marginTop: 10 }}>{mgrFbMut.isPending ? "Submitting…" : "Submit Feedback"}</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Goal Form (top-level to avoid TS nested component errors) ────────────────

function GoalForm({ initial, onSave, onCancel, pending }: {
  initial?: any; onSave: (d: any) => void; onCancel: () => void; pending?: boolean;
}) {
  const [f, setF] = useState(initial || { title: "", description: "", due_date: "", status: "pending", progress_pct: 0 });
  const [titleError, setTitleError] = useState<string | null>(null);
  return (
    <div style={{ ...card, border: "1.5px solid #bfdbfe", marginBottom: 16 }}>
      <div style={{ display: "grid", gap: 12 }}>
        <div><label style={lbl}>Goal Title *</label>
          <input value={f.title} onChange={e => { setF((x: any) => ({...x, title: e.target.value})); setTitleError(null); }} placeholder="e.g. Complete AWS certification" style={inp} />
          {titleError && <div style={{ color: "#dc2626", fontSize: 12, marginTop: 4 }}>{titleError}</div>}</div>
        <div><label style={lbl}>Description</label>
          <textarea value={f.description || ""} rows={2} onChange={e => setF((x: any) => ({...x, description: e.target.value}))} placeholder="What does success look like?" style={{ ...inp, resize: "vertical" }} /></div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
          <div><label style={lbl}>Due Date</label>
            <DateInput value={f.due_date || ""} onChange={e => setF((x: any) => ({...x, due_date: e.target.value}))} style={inp} /></div>
          <div><label style={lbl}>Status</label>
            <select value={f.status} onChange={e => setF((x: any) => ({...x, status: e.target.value}))} style={inp}>
              {Object.keys(GOAL_STATUS_COLORS).map(s => <option key={s} value={s}>{s.replace("_"," ")}</option>)}
            </select></div>
          <div><label style={lbl}>Progress: {f.progress_pct}%</label>
            <input type="range" min={0} max={100} value={f.progress_pct} onChange={e => setF((x: any) => ({...x, progress_pct: parseInt(e.target.value)}))} style={{ width: "100%", marginTop: 10 }} /></div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => {
            const err = requiredTrimmed(f.title, "Goal title");
            setTitleError(err);
            if (!err) onSave({ ...f, title: f.title.trim() });
          }} disabled={pending} style={primaryBtn}>{pending ? "Saving…" : "Save Goal"}</button>
          <button onClick={onCancel} style={{ ...outlineBtn, borderColor: "#e2e8f0", color: "#64748b" }}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Standalone Goals Tab ───────────────────────────────────────────────────────

function GoalsTab({ employees, isAdmin }: { employees: any[]; isAdmin: boolean }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const confirm = useConfirm();
  const [showForm, setShowForm] = useState(false);
  const [editGoal, setEditGoal] = useState<any>(null);
  const [filter, setFilter] = useState("all");
  const [viewTeam, setViewTeam] = useState(false);

  const { data: myGoals = [] } = useQuery({ queryKey: ["my-goals"], queryFn: P.getMyGoals });
  const { data: teamGoals = [] } = useQuery({ queryKey: ["team-goals"], queryFn: P.getTeamGoals, enabled: isAdmin && viewTeam });

  const goals = (viewTeam && isAdmin ? teamGoals : myGoals) as any[];
  const filtered = filter === "all" ? goals : goals.filter((g: any) => g.status === filter);

  const createMut = useMutation({
    mutationFn: P.createGoal,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["my-goals"] }); setShowForm(false); toast("Goal created"); },
    onError: (e: any) => toast(getErrorMessage(e, "Failed to create goal"), "error"),
  });
  const updateMut = useMutation({
    mutationFn: ({ id, d }: any) => P.updateGoal(id, d),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["my-goals"] }); qc.invalidateQueries({ queryKey: ["team-goals"] }); setEditGoal(null); toast("Goal updated"); },
  });
  const deleteMut = useMutation({
    mutationFn: P.deleteGoal,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["my-goals"] }); qc.invalidateQueries({ queryKey: ["team-goals"] }); toast("Goal deleted"); },
    onError: (e: any) => toast(getErrorMessage(e, "Failed"), "error"),
  });

  const statusCounts = { all: goals.length, pending: 0, in_progress: 0, met: 0, partial: 0, missed: 0 } as Record<string, number>;
  goals.forEach((g: any) => { if (g.status in statusCounts) statusCounts[g.status]++; });

  return (
    <div>
      <LoadingOverlay active={createMut.isPending || updateMut.isPending || deleteMut.isPending} />
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {isAdmin && (
            <div style={{ display: "flex", background: "#f1f5f9", borderRadius: 8, padding: 3 }}>
              {[["mine", "My Goals"], ["team", "Team Goals"]].map(([k, label]) => (
                <button key={k} onClick={() => setViewTeam(k === "team")}
                  style={{ padding: "5px 12px", borderRadius: 6, border: "none", fontSize: 12, fontWeight: 600, cursor: "pointer", background: (k === "team") === viewTeam ? "white" : "transparent", color: (k === "team") === viewTeam ? NAVY : "#64748b", boxShadow: (k === "team") === viewTeam ? "0 1px 3px rgba(0,0,0,0.1)" : "none" }}>
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>
        {!viewTeam && (
          <button onClick={() => { setShowForm(true); setEditGoal(null); }} style={primaryBtn}>+ New Goal</button>
        )}
      </div>

      {/* Status filter pills */}
      <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
        {Object.entries(statusCounts).map(([s, cnt]) => (
          <button key={s} onClick={() => setFilter(s)} style={{
            padding: "4px 12px", borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: "pointer", border: "1.5px solid",
            background: filter === s ? GOAL_STATUS_COLORS[s] || NAVY : "white",
            color: filter === s ? "white" : GOAL_STATUS_COLORS[s] || "#64748b",
            borderColor: GOAL_STATUS_COLORS[s] || "#e2e8f0",
          }}>{s === "all" ? "All" : s.replace("_"," ")} ({cnt})</button>
        ))}
      </div>

      {/* Create form */}
      {showForm && !editGoal && (
        <GoalForm onSave={d => createMut.mutate(d)} onCancel={() => setShowForm(false)} pending={createMut.isPending} />
      )}

      {/* Goal cards */}
      <div style={{ display: "grid", gap: 10 }}>
        {filtered.length === 0 && !showForm && (
          <div style={{ textAlign: "center", padding: "60px 0", color: "#94a3b8" }}>
            <div style={{ fontSize: 40 }}>🎯</div>
            <div style={{ marginTop: 10, fontSize: 14 }}>No goals yet. Create your first goal to track your progress.</div>
          </div>
        )}
        {filtered.map((g: any) => (
          editGoal?.id === g.id ? (
            <div key={g.id}><GoalForm initial={editGoal} onSave={(d: any) => updateMut.mutate({ id: g.id, d })} onCancel={() => setEditGoal(null)} pending={updateMut.isPending} /></div>
          ) : (
            <div key={g.id} style={{ ...card, borderLeft: `4px solid ${GOAL_STATUS_COLORS[g.status] || "#e2e8f0"}` }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: "#1e293b", marginBottom: 2 }}>{g.title}</div>
                  {g.description && <div style={{ fontSize: 12, color: "#64748b" }}>{g.description}</div>}
                  {viewTeam && <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>Owner: {g.owner_name}</div>}
                </div>
                <div style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0, marginLeft: 12 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 12, background: `${GOAL_STATUS_COLORS[g.status]}20`, color: GOAL_STATUS_COLORS[g.status], border: `1px solid ${GOAL_STATUS_COLORS[g.status]}40` }}>
                    {g.status.replace("_"," ")}
                  </span>
                  {(!viewTeam || isAdmin) && (
                    <>
                      <button onClick={() => setEditGoal(g)} style={{ ...outlineBtn, fontSize: 11, padding: "3px 8px" }}>Edit</button>
                      <button onClick={async () => {
                        if (await confirm(`Delete the goal "${g.title}"? This cannot be undone.`,
                          { title: "Delete goal", confirmLabel: "Delete" })) deleteMut.mutate(g.id);
                      }} style={{ background: "none", border: "none", color: "#dc2626", cursor: "pointer", fontSize: 18, padding: "0 4px" }}>×</button>
                    </>
                  )}
                </div>
              </div>
              <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#64748b", marginBottom: 4 }}>
                    <span>Progress</span>
                    <span style={{ fontWeight: 700, color: GOAL_STATUS_COLORS[g.status] }}>{g.progress_pct}%</span>
                  </div>
                  <ProgressBar pct={g.progress_pct} color={GOAL_STATUS_COLORS[g.status]} />
                </div>
                {g.due_date && (
                  <div style={{ fontSize: 11, color: "#64748b", flexShrink: 0, textAlign: "right" }}>
                    <div>Due</div>
                    <div style={{ fontWeight: 600, color: new Date(g.due_date) < new Date() && g.status !== "met" ? "#dc2626" : "#1e293b" }}>{g.due_date}</div>
                  </div>
                )}
              </div>
            </div>
          )
        ))}
      </div>
    </div>
  );
}

// ── Goal Summary Card (in My Reviews) ─────────────────────────────────────────

function GoalSummaryCard({ goals }: { goals: any[] }) {
  if (!goals.length) return null;
  const counts = { met: 0, in_progress: 0, partial: 0, missed: 0, pending: 0 } as Record<string, number>;
  goals.forEach(g => { if (g.status in counts) counts[g.status]++; });
  const avgProgress = Math.round(goals.reduce((s, g) => s + (g.progress_pct || 0), 0) / goals.length);

  return (
    <div style={{ ...card, marginBottom: 20, border: "1px solid #e2e8f0" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: NAVY }}>🎯 My Goals</h3>
        <span style={{ fontSize: 13, color: "#64748b" }}>{goals.length} goals · avg {avgProgress}%</span>
      </div>
      <div style={{ display: "flex", gap: 16, marginBottom: 12, flexWrap: "wrap" }}>
        {Object.entries(counts).filter(([, v]) => v > 0).map(([status, count]) => (
          <div key={status} style={{ textAlign: "center" }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: GOAL_STATUS_COLORS[status] }}>{count}</div>
            <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 1 }}>{status.replace("_"," ")}</div>
          </div>
        ))}
      </div>
      <ProgressBar pct={avgProgress} />
    </div>
  );
}

// ── Rating History Chart ───────────────────────────────────────────────────────

function RatingHistoryCard({ userId }: { userId: number }) {
  const { data: analytics }: any = useQuery({ queryKey: ["perf-analytics"], queryFn: P.getAnalytics });
  if (!analytics) return null;
  const myTrend = analytics.employee_trends?.find((t: any) => t.user_id === userId);
  if (!myTrend || myTrend.ratings?.length < 2) return null;
  const data = myTrend.ratings.map((r: any) => ({ cycle: r.cycle?.split(" ")[0] || r.cycle_id, rating: r.rating }));
  return (
    <div style={{ ...card, marginBottom: 20 }}>
      <h3 style={{ margin: "0 0 12px", fontSize: 14, fontWeight: 700, color: NAVY }}>📈 My Rating History</h3>
      <ResponsiveContainer width="100%" height={140}>
        <LineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
          <XAxis dataKey="cycle" tick={{ fontSize: 11 }} />
          <YAxis domain={[1, 5]} tick={{ fontSize: 11 }} />
          <Tooltip formatter={(v: any) => [Number(v).toFixed(1), "Rating"]} />
          <Line type="monotone" dataKey="rating" stroke={NAVY} strokeWidth={2} dot={{ fill: NAVY, r: 5 }} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── Calibration View ───────────────────────────────────────────────────────────

function CalibrationView({ cycleId }: { cycleId: number }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const { data: cal }: any = useQuery({ queryKey: ["calibration", cycleId], queryFn: () => P.calibrationView(cycleId) });
  const [editing, setEditing] = useState<Record<number, { hr_final_rating: number; hr_notes: string }>>({});

  const calMut = useMutation({
    mutationFn: ({ id, d }: any) => api.patch(`/api/performance/reviews/${id}/calibrate`, d).then(r => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["calibration", cycleId] }); toast("Calibration saved"); },
    onError: () => toast("Failed to save calibration", "error"),
  });

  if (!cal) return <div style={{ padding: 20, textAlign: "center", color: "#94a3b8" }}>Loading…</div>;

  const RATING_COLORS = ["#ef4444","#f59e0b","#3b82f6","#10b981","#8b5cf6"];

  return (
    <div>
      <LoadingOverlay active={calMut.isPending} />
      {/* Summary bar */}
      <div style={{ display: "flex", gap: 16, marginBottom: 16, flexWrap: "wrap" }}>
        <div style={{ ...card, flex: "1 1 120px", textAlign: "center" }}>
          <div style={{ fontSize: 24, fontWeight: 800, color: NAVY }}>{cal.summary.rated}</div>
          <div style={{ fontSize: 12, color: "#64748b" }}>Rated</div>
        </div>
        <div style={{ ...card, flex: "1 1 120px", textAlign: "center" }}>
          <div style={{ fontSize: 24, fontWeight: 800, color: "#10b981" }}>{cal.summary.avg_rating || "—"}</div>
          <div style={{ fontSize: 12, color: "#64748b" }}>Avg Rating</div>
        </div>
        <div style={{ ...card, flex: "3 1 300px" }}>
          <div style={{ fontSize: 12, color: "#64748b", marginBottom: 8 }}>Distribution</div>
          <ResponsiveContainer width="100%" height={60}>
            <BarChart data={cal.summary.distribution} margin={{ top: 0, bottom: 0, left: 0, right: 0 }}>
              <XAxis dataKey="rating" tick={{ fontSize: 10 }} />
              <YAxis hide />
              <Tooltip formatter={(v: any) => [v, "Reviews"]} />
              <Bar dataKey="count" radius={[3,3,0,0]}>
                {cal.summary.distribution.map((_: any, i: number) => (
                  <Cell key={i} fill={RATING_COLORS[i]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Review table */}
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ background: "#f8fafc", borderBottom: "2px solid #e2e8f0" }}>
              {["Employee","Dept","Reviewer","Self","Mgr","HR Calibrated","Status","Recommendation"].map(h => (
                <th key={h} style={{ padding: "8px 12px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#64748b" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {cal.rows.map((r: any) => {
              const ed = editing[r.review_id];
              return (
                <tr key={r.review_id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                  <td style={{ padding: "8px 12px", fontWeight: 600, color: "#1e293b" }}>{r.employee_name}</td>
                  <td style={{ padding: "8px 12px", color: "#64748b", fontSize: 12 }}>{r.employee_department || "—"}</td>
                  <td style={{ padding: "8px 12px", color: "#64748b", fontSize: 12 }}>{r.reviewer_name || "—"}</td>
                  <td style={{ padding: "8px 12px" }}>{r.self_rating ? <span style={{ color: "#f59e0b", fontWeight: 700 }}>{"★".repeat(Math.round(r.self_rating))} {r.self_rating.toFixed(1)}</span> : "—"}</td>
                  <td style={{ padding: "8px 12px" }}>{r.manager_rating ? <span style={{ color: "#2563eb", fontWeight: 700 }}>{"★".repeat(Math.round(r.manager_rating))} {r.manager_rating.toFixed(1)}</span> : "—"}</td>
                  <td style={{ padding: "8px 12px" }}>
                    {ed !== undefined ? (
                      <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                        <select value={ed.hr_final_rating} onChange={e => setEditing(x => ({...x, [r.review_id]: {...ed, hr_final_rating: Number(e.target.value)}}))}
                          style={{ ...inp, width: 60, padding: "4px 6px", fontSize: 12 }}>
                          <option value={0}>—</option>
                          {[1,2,3,4,5].map(n => <option key={n} value={n}>{n}</option>)}
                        </select>
                        <button onClick={() => { calMut.mutate({ id: r.review_id, d: ed }); setEditing(x => { const nx = {...x}; delete nx[r.review_id]; return nx; }); }}
                          disabled={calMut.isPending}
                          style={{ background: NAVY, color: "white", border: "none", borderRadius: 4, padding: "3px 8px", fontSize: 11, cursor: "pointer" }}>✓</button>
                        <button onClick={() => setEditing(x => { const nx = {...x}; delete nx[r.review_id]; return nx; })}
                          style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8" }}>✕</button>
                      </div>
                    ) : (
                      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                        <span style={{ color: r.hr_final_rating ? "#7c3aed" : "#94a3b8", fontWeight: r.hr_final_rating ? 700 : 400 }}>
                          {r.hr_final_rating ? `★ ${r.hr_final_rating.toFixed(1)}` : "—"}
                        </span>
                        <button onClick={() => setEditing(x => ({...x, [r.review_id]: { hr_final_rating: r.hr_final_rating || 0, hr_notes: r.hr_notes || "" }}))}
                          style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, color: "#64748b" }}>✏</button>
                      </div>
                    )}
                  </td>
                  <td style={{ padding: "8px 12px" }}>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 7px", borderRadius: 10, background: STATUS_REVIEW[r.status]?.bg || "#f1f5f9", color: STATUS_REVIEW[r.status]?.color || "#64748b" }}>{STATUS_REVIEW[r.status]?.label || r.status}</span>
                  </td>
                  <td style={{ padding: "8px 12px", fontSize: 11 }}>
                    {r.promotion_recommendation && r.promotion_recommendation !== "none" ? (
                      <span style={{ color: "#166534", fontWeight: 600 }}>{r.promotion_recommendation}{r.increment_pct ? ` +${r.increment_pct}%` : ""}</span>
                    ) : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Cycle Progress Card ───────────────────────────────────────────────────────

function CycleProgressCard({ cycleId, onRemind }: { cycleId: number; onRemind: () => void }) {
  const { data: progress }: any = useQuery({ queryKey: ["cycle-progress", cycleId], queryFn: () => P.cycleProgress(cycleId), refetchInterval: 30000 });
  if (!progress) return null;
  return (
    <div style={{ ...card, marginBottom: 16, border: "1.5px solid #bfdbfe" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <h4 style={{ margin: 0, color: NAVY, fontSize: 13, fontWeight: 700 }}>Cycle Progress</h4>
        {progress.not_submitted?.length > 0 && (
          <button onClick={onRemind} style={{ ...outlineBtn, fontSize: 11, padding: "4px 10px", color: "#d97706", borderColor: "#fde68a" }}>
            Send Reminders ({progress.not_submitted.length})
          </button>
        )}
      </div>
      <div style={{ display: "flex", gap: 16, marginBottom: 10 }}>
        {[["Total", progress.total, "#1e293b"],["Self Done", progress.self_submitted, "#2563eb"],["Completed", progress.completed, "#16a34a"],["Overdue", progress.overdue_count, "#dc2626"]].map(([label, value, color]) => (
          <div key={label as string} style={{ textAlign: "center" }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: color as string }}>{value}</div>
            <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 1 }}>{label}</div>
          </div>
        ))}
      </div>
      <ProgressBar pct={progress.completion_pct || 0} />
      <div style={{ fontSize: 11, color: "#64748b", marginTop: 4, textAlign: "right" }}>{progress.completion_pct}% complete</div>
      {progress.not_submitted?.length > 0 && (
        <div style={{ marginTop: 8, fontSize: 12, color: "#94a3b8" }}>Pending: {progress.not_submitted.slice(0, 5).map((e: any) => e.employee_name).join(", ")}{progress.not_submitted.length > 5 ? ` +${progress.not_submitted.length - 5} more` : ""}</div>
      )}
    </div>
  );
}

// ── Cycles Tab ────────────────────────────────────────────────────────────────

function CyclesTab({ employees }: { employees: any[] }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const confirm = useConfirm();
  const { user } = useAuth();
  const BLANK = { title: "", review_type: "annual", period_start: "", period_end: "", due_date: "", peer_feedback_enabled: false, upward_feedback_enabled: false, carry_forward_goals: false };
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [selectedCycle, setSelectedCycle] = useState<number | null>(null);
  const [openReview, setOpenReview] = useState<any>(null);
  const [showCalibration, setShowCalibration] = useState(false);

  const { data: cycles = [] } = useQuery({ queryKey: ["review-cycles"], queryFn: P.getCycles });
  const { data: reviews = [] } = useQuery({ queryKey: ["cycle-reviews", selectedCycle], queryFn: () => P.getReviews(selectedCycle!), enabled: !!selectedCycle });

  const createMut = useMutation({ mutationFn: P.createCycle, onSuccess: () => { qc.invalidateQueries({ queryKey: ["review-cycles"] }); setShowCreate(false); setForm(BLANK); toast("Cycle created"); }, onError: () => toast("Failed", "error") });
  const activateMut = useMutation({ mutationFn: P.activateCycle, onSuccess: (d: any) => { qc.invalidateQueries({ queryKey: ["review-cycles","cycle-reviews"] }); toast(`Activated — ${d.reviews_created} reviews created`); }, onError: (e: any) => toast(e.response?.data?.detail || "Failed", "error") });
  const closeMut = useMutation({ mutationFn: (id: number) => P.updateCycle(id, { status: "closed" }), onSuccess: () => { qc.invalidateQueries({ queryKey: ["review-cycles"] }); toast("Cycle closed"); } });
  const deleteMut = useMutation({ mutationFn: P.deleteCycle, onSuccess: () => { qc.invalidateQueries({ queryKey: ["review-cycles"] }); setSelectedCycle(null); toast("Cycle deleted"); }, onError: (e: any) => toast(e.response?.data?.detail || "Cannot delete closed cycle", "error") });
  const remindMut = useMutation({ mutationFn: P.sendReminders, onSuccess: (d: any) => toast(`${d.sent} reminder emails sent`), onError: () => toast("Failed", "error") });

  return (
    <div style={{ display: "grid", gridTemplateColumns: selectedCycle ? "300px 1fr" : "1fr", gap: 20 }}>
      <LoadingOverlay active={
        createMut.isPending || activateMut.isPending || closeMut.isPending ||
        deleteMut.isPending || remindMut.isPending
      } />
      {/* Cycle list */}
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <span style={{ fontSize: 13, color: "#64748b" }}>{(cycles as any[]).length} cycles</span>
          <button onClick={() => setShowCreate(s => !s)} style={primaryBtn}>+ New Cycle</button>
        </div>

        {showCreate && (
          <div style={{ ...card, marginBottom: 14, border: "1.5px solid #bfdbfe" }}>
            <h4 style={{ margin: "0 0 12px", color: NAVY, fontSize: 14 }}>New Review Cycle</h4>
            <div style={{ display: "grid", gap: 10 }}>
              <div><label style={lbl}>Title *</label><input value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))} placeholder="e.g. Q2 2026 Review" style={inp} /></div>
              <div><label style={lbl}>Type</label><select value={form.review_type} onChange={e => setForm(f => ({...f, review_type: e.target.value}))} style={inp}>{REVIEW_TYPES.map(t => <option key={t} value={t}>{t.replace("_"," ").replace(/\b\w/g, c => c.toUpperCase())}</option>)}</select></div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                <div><label style={lbl}>Period Start</label><DateInput value={form.period_start} onChange={e => setForm(f => ({...f, period_start: e.target.value}))} style={inp} /></div>
                <div><label style={lbl}>Period End</label><DateInput value={form.period_end} onChange={e => setForm(f => ({...f, period_end: e.target.value}))} style={inp} /></div>
              </div>
              <div><label style={lbl}>Due Date</label><DateInput value={form.due_date} onChange={e => setForm(f => ({...f, due_date: e.target.value}))} style={inp} /></div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {[["peer_feedback_enabled","Enable 360° peer feedback"],["upward_feedback_enabled","Enable upward (manager) feedback"],["carry_forward_goals","Carry forward unmet goals"]].map(([k, label]) => (
                  <label key={k} style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 12, cursor: "pointer" }}>
                    <input type="checkbox" checked={(form as any)[k]} onChange={e => setForm(f => ({...f, [k]: e.target.checked}))} />
                    {label}
                  </label>
                ))}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => createMut.mutate(form)} disabled={!form.title || createMut.isPending} style={primaryBtn}>{createMut.isPending ? "Creating…" : "Create"}</button>
                <button onClick={() => setShowCreate(false)} style={{ ...outlineBtn, borderColor: "#e2e8f0", color: "#64748b" }}>Cancel</button>
              </div>
            </div>
          </div>
        )}

        <div style={{ display: "grid", gap: 10 }}>
          {(cycles as any[]).map((c: any) => {
            const st = STATUS_CYCLE[c.status] || STATUS_CYCLE.draft;
            const isSelected = selectedCycle === c.id;
            return (
              <div key={c.id} onClick={() => setSelectedCycle(isSelected ? null : c.id)} style={{ ...card, cursor: "pointer", border: `1.5px solid ${isSelected ? NAVY : "#e2e8f0"}`, background: isSelected ? "#f0f4ff" : "white" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: "#1e293b" }}>{c.title}</div>
                    <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 2 }}>
                      {c.review_type.replace("_"," ").replace(/\b\w/g, (ch: string) => ch.toUpperCase())}
                      {c.due_date ? ` · Due ${c.due_date}` : ""} · {c.review_count} reviews
                      {c.peer_feedback_enabled ? " · 360°" : ""}{c.upward_feedback_enabled ? " · Upward" : ""}{c.carry_forward_goals ? " · CF" : ""}
                    </div>
                  </div>
                  <span style={{ background: st.bg, color: st.color, padding: "2px 8px", borderRadius: 12, fontSize: 11, fontWeight: 700, flexShrink: 0, marginLeft: 8 }}>{st.label}</span>
                </div>
                <div style={{ display: "flex", gap: 6, marginTop: 10, flexWrap: "wrap" }}>
                  {c.status === "draft" && <button onClick={async e => {
                    e.stopPropagation();
                    const ok = await confirm(`Activate "${c.title}"? This will immediately email every employee and manager included in this cycle.`, { title: "Activate cycle", confirmLabel: "Activate", danger: false });
                    if (ok) activateMut.mutate(c.id);
                  }} disabled={activateMut.isPending} style={{ fontSize: 12, fontWeight: 600, padding: "4px 10px", borderRadius: 6, border: "none", background: "#dcfce7", color: "#166534", cursor: "pointer" }}>Activate</button>}
                  {c.status === "active" && <>
                    <button onClick={e => { e.stopPropagation(); closeMut.mutate(c.id); }} disabled={closeMut.isPending} style={{ fontSize: 12, fontWeight: 600, padding: "4px 10px", borderRadius: 6, border: "none", background: "#fee2e2", color: "#dc2626", cursor: "pointer" }}>Close</button>
                    <button onClick={async e => {
                      e.stopPropagation();
                      const ok = await confirm(`Send a reminder email to every pending reviewer in "${c.title}"?`, { title: "Send reminders", confirmLabel: "Remind All", danger: false });
                      if (ok) remindMut.mutate(c.id);
                    }} disabled={remindMut.isPending} style={{ fontSize: 12, fontWeight: 600, padding: "4px 10px", borderRadius: 6, border: "1px solid #fde68a", background: "#fffbeb", color: "#92400e", cursor: "pointer" }}>Remind All</button>
                  </>}
                  {c.status === "draft" && <button onClick={async e => {
                    e.stopPropagation();
                    if (await confirm(`Delete the draft cycle "${c.title}"? This cannot be undone.`,
                      { title: "Delete cycle", confirmLabel: "Delete" })) deleteMut.mutate(c.id);
                  }} disabled={deleteMut.isPending} style={{ fontSize: 12, padding: "4px 10px", borderRadius: 6, border: "1px solid #e2e8f0", background: "none", color: "#94a3b8", cursor: "pointer" }}>Delete</button>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Cycle detail */}
      {selectedCycle && (() => {
        const cycle = (cycles as any[]).find((c: any) => c.id === selectedCycle);
        return (
          <div>
            {cycle?.status === "active" && <CycleProgressCard cycleId={selectedCycle} onRemind={() => remindMut.mutate(selectedCycle)} />}
            {(cycle?.status === "active" || cycle?.status === "closed") && (
              <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
                <button onClick={() => setShowCalibration(false)} style={{ ...outlineBtn, fontSize: 12, padding: "5px 12px", background: !showCalibration ? "#f0f4ff" : "white" }}>Reviews</button>
                <button onClick={() => setShowCalibration(true)} style={{ ...outlineBtn, fontSize: 12, padding: "5px 12px", background: showCalibration ? "#f0f4ff" : "white" }}>Calibration</button>
              </div>
            )}
            {showCalibration ? (
              <CalibrationView cycleId={selectedCycle} />
            ) : (
              <>
                <h3 style={{ margin: "0 0 12px", color: NAVY, fontSize: 14 }}>Reviews ({(reviews as any[]).length})</h3>
                <div style={{ display: "grid", gap: 8 }}>
                  {(reviews as any[]).map((r: any) => {
                    const st = STATUS_REVIEW[r.status] || STATUS_REVIEW.pending;
                    const effRating = r.hr_final_rating || r.final_rating;
                    return (
                      <div key={r.id} style={{ ...card, cursor: "pointer" }} onClick={() => setOpenReview(r)}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 14 }}>{r.employee_name}</div>
                            <div style={{ fontSize: 12, color: "#94a3b8" }}>{r.employee_designation || ""}{r.reviewer_name ? ` · ${r.reviewer_name}` : ""}</div>
                          </div>
                          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                            {effRating > 0 && <StarRating value={effRating} readonly size={18} />}
                            {r.promotion_recommendation && r.promotion_recommendation !== "none" && <span style={{ fontSize: 10, background: "#f0fdf4", color: "#166534", border: "1px solid #86efac", borderRadius: 8, padding: "1px 6px" }}>{r.promotion_recommendation}</span>}
                            <span style={{ background: st.bg, color: st.color, padding: "2px 8px", borderRadius: 12, fontSize: 11, fontWeight: 700 }}>{st.label}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {(reviews as any[]).length === 0 && <div style={{ textAlign: "center", padding: "40px 0", color: "#94a3b8" }}>Activate the cycle to generate review records.</div>}
              </>
            )}
          </div>
        );
      })()}

      {openReview && <ReviewModal review={openReview} userId={user?.id || 0} userRole={user?.role || "employee"} employees={employees} onClose={() => setOpenReview(null)} />}
    </div>
  );
}

// ── My Reviews Tab ────────────────────────────────────────────────────────────

function MyReviewsTab({ employees }: { employees: any[] }) {
  const { user } = useAuth();
  const [openReview, setOpenReview] = useState<any>(null);
  const { data: myReviews = [] } = useQuery({ queryKey: ["my-reviews"], queryFn: P.getMyReviews });
  const { data: toAction = [] } = useQuery({ queryKey: ["reviews-to-action"], queryFn: P.getToAction });
  const { data: myGoals = [] } = useQuery({ queryKey: ["my-goals"], queryFn: P.getMyGoals });

  const allReviews = [
    ...(myReviews as any[]).map((r: any) => ({ ...r, _role: "self" })),
    ...(toAction as any[]).filter((r: any) => r.employee_id !== user?.id).map((r: any) => ({ ...r, _role: "reviewer" })),
  ];

  return (
    <div style={{ maxWidth: 780 }}>
      <AnytimeFeedbackSection employees={employees} />
      <GoalSummaryCard goals={myGoals as any[]} />
      <RatingHistoryCard userId={user?.id || 0} />

      {allReviews.length === 0 ? (
        <div style={{ textAlign: "center", padding: "60px 0", color: "#94a3b8" }}>
          <div style={{ fontSize: 48 }}>📝</div>
          <div style={{ marginTop: 12, fontSize: 15 }}>No performance reviews yet.</div>
          <div style={{ marginTop: 6, fontSize: 13 }}>Your manager will create a review cycle and add you.</div>
        </div>
      ) : (
        <div style={{ display: "grid", gap: 10 }}>
          {allReviews.map((r: any) => {
            const st = STATUS_REVIEW[r.status] || STATUS_REVIEW.pending;
            const isSelf = r._role === "self";
            const needsAction = (isSelf && r.status === "pending") || (!isSelf && r.status === "self_done") || (isSelf && r.status === "completed");
            const effRating = r.hr_final_rating || r.final_rating;
            return (
              <div key={`${r.id}-${r._role}`} style={{ ...card, cursor: "pointer", border: `1.5px solid ${needsAction ? "#fde68a" : "#e2e8f0"}`, background: needsAction ? "#fffbeb" : "white" }} onClick={() => setOpenReview(r)}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 3 }}>
                      <span style={{ fontWeight: 700, fontSize: 14, color: "#1e293b" }}>{r.cycle_title}</span>
                      <span style={{ fontSize: 11, background: isSelf ? "#eff6ff" : "#f0fdf4", color: isSelf ? "#1d4ed8" : "#166534", padding: "1px 8px", borderRadius: 10, fontWeight: 600 }}>
                        {isSelf ? "My Review" : `Review: ${r.employee_name}`}
                      </span>
                      {r.cycle_peer_feedback_enabled && <span style={{ fontSize: 10, background: "#fdf4ff", color: "#7e22ce", padding: "1px 6px", borderRadius: 8, fontWeight: 600 }}>360°</span>}
                      {r.cycle_upward_feedback_enabled && <span style={{ fontSize: 10, background: "#fff7ed", color: "#c2410c", padding: "1px 6px", borderRadius: 8, fontWeight: 600 }}>Upward</span>}
                    </div>
                    <div style={{ fontSize: 12, color: "#94a3b8" }}>
                      {r.cycle_type?.replace("_"," ").replace(/\b\w/g, (c: string) => c.toUpperCase())}
                      {r.cycle_due_date ? ` · Due ${r.cycle_due_date}` : ""}
                      {isSelf && r.reviewer_name ? ` · Reviewer: ${r.reviewer_name}` : ""}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    {effRating > 0 && <StarRating value={effRating} readonly size={20} />}
                    <span style={{ background: st.bg, color: st.color, padding: "2px 8px", borderRadius: 12, fontSize: 11, fontWeight: 700 }}>{st.label}</span>
                    {needsAction && <span style={{ fontSize: 10, background: "#fef9c3", color: "#854d0e", border: "1px solid #fde047", padding: "1px 6px", borderRadius: 8, fontWeight: 700 }}>Action needed</span>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {openReview && <ReviewModal review={openReview} userId={user?.id || 0} userRole={user?.role || "employee"} employees={employees} onClose={() => setOpenReview(null)} />}
    </div>
  );
}

// ── Analytics Tab ─────────────────────────────────────────────────────────────

function AnalyticsTab() {
  const { data: analytics }: any = useQuery({ queryKey: ["perf-analytics"], queryFn: P.getAnalytics });
  if (!analytics) return <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>Loading analytics…</div>;
  const RATING_COLORS = ["#ef4444","#f59e0b","#3b82f6","#10b981","#8b5cf6"];
  return (
    <div style={{ display: "grid", gap: 24 }}>
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
        <div style={{ ...card, flex: "1 1 150px", textAlign: "center" }}>
          <div style={{ fontSize: 32, fontWeight: 800, color: NAVY }}>{analytics.total_completed_reviews}</div>
          <div style={{ fontSize: 13, color: "#64748b", marginTop: 4 }}>Completed Reviews</div>
        </div>
        <div style={{ ...card, flex: "1 1 150px", textAlign: "center" }}>
          <div style={{ fontSize: 32, fontWeight: 800, color: "#10b981" }}>
            {analytics.dept_averages?.length > 0 ? (analytics.dept_averages.reduce((s: number, d: any) => s + d.avg_rating, 0) / analytics.dept_averages.length).toFixed(1) : "—"}
          </div>
          <div style={{ fontSize: 13, color: "#64748b", marginTop: 4 }}>Org Average Rating</div>
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
        <div style={card}>
          <h3 style={{ margin: "0 0 14px", fontSize: 14, color: NAVY }}>Rating Distribution</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={analytics.rating_distribution}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="rating" tickFormatter={(v: number) => RATING_LABELS[v]?.label?.split(" ")[0] || v} tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v: any) => [v, "Reviews"]} labelFormatter={(l: any) => RATING_LABELS[l]?.label || l} />
              <Bar dataKey="count" radius={[4,4,0,0]}>
                {analytics.rating_distribution.map((_: any, i: number) => <rect key={i} fill={RATING_COLORS[i]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div style={card}>
          <h3 style={{ margin: "0 0 14px", fontSize: 14, color: NAVY }}>Avg by Department</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={analytics.dept_averages} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis type="number" domain={[0, 5]} tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="department" tick={{ fontSize: 11 }} width={90} />
              <Tooltip formatter={(v: any) => [Number(v).toFixed(2), "Avg"]} />
              <Bar dataKey="avg_rating" fill={TEAL} radius={[0,4,4,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div style={card}>
        <h3 style={{ margin: "0 0 14px", fontSize: 14, color: NAVY }}>Cycle Completion</h3>
        <div style={{ display: "grid", gap: 8 }}>
          {analytics.cycle_completion.map((c: any) => (
            <div key={c.cycle_id}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
                <span style={{ fontWeight: 500 }}>{c.title}</span>
                <span style={{ color: "#64748b" }}>{c.completed}/{c.total} · {c.pct}%</span>
              </div>
              <ProgressBar pct={c.pct} color={c.pct >= 80 ? "#16a34a" : c.pct >= 50 ? "#f59e0b" : "#ef4444"} height={6} />
            </div>
          ))}
        </div>
      </div>
      <div style={card}>
        <h3 style={{ margin: "0 0 4px", fontSize: 14, color: NAVY }}>9-Box Talent Grid</h3>
        <p style={{ margin: "0 0 16px", fontSize: 12, color: "#64748b" }}>Performance × Potential — based on latest completed review per employee</p>
        <NineBoxGrid data={analytics.nine_box || []} />
      </div>
    </div>
  );
}

// ── Anytime Feedback Section ──────────────────────────────────────────────────

function AnytimeFeedbackSection({ employees }: { employees: any[] }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();
  const [showForm, setShowForm] = useState(false);
  const [activeView, setActiveView] = useState<"received" | "given">("received");
  const [form, setForm] = useState({ receiver_id: "", message: "", is_anonymous: false });

  const { data: received = [] } = useQuery({ queryKey: ["anytime-feedback-received"], queryFn: P.getAnytimeFeedback });
  const { data: given = [] } = useQuery({ queryKey: ["anytime-feedback-given"], queryFn: P.getGivenFeedback });

  const giveMut = useMutation({
    mutationFn: P.giveAnytimeFeedback,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["anytime-feedback-given"] });
      setShowForm(false);
      setForm({ receiver_id: "", message: "", is_anonymous: false });
      toast("Feedback sent");
    },
    onError: (e: any) => toast(e.response?.data?.detail || "Failed to send", "error"),
  });

  const items = (activeView === "received" ? received : given) as any[];

  return (
    <div style={{ ...card, marginBottom: 20 }}>
      <LoadingOverlay active={giveMut.isPending} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: NAVY }}>💬 Feedback</h3>
          <div style={{ display: "flex", background: "#f1f5f9", borderRadius: 8, padding: 3 }}>
            {([["received", "Received"], ["given", "Given"]] as const).map(([k, label]) => (
              <button key={k} onClick={() => setActiveView(k)}
                style={{ padding: "4px 10px", borderRadius: 6, border: "none", fontSize: 11, fontWeight: 600, cursor: "pointer",
                  background: activeView === k ? "white" : "transparent",
                  color: activeView === k ? NAVY : "#64748b",
                  boxShadow: activeView === k ? "0 1px 3px rgba(0,0,0,0.1)" : "none" }}>
                {label} {activeView === k && `(${items.length})`}
              </button>
            ))}
          </div>
        </div>
        {!showForm && (
          <button onClick={() => setShowForm(true)} style={{ ...primaryBtn, fontSize: 12, padding: "6px 14px" }}>+ Give Feedback</button>
        )}
      </div>

      {showForm && (
        <div style={{ background: "#f8fafc", borderRadius: 10, padding: 16, marginBottom: 16, border: "1.5px solid #bfdbfe" }}>
          <div style={{ display: "grid", gap: 10 }}>
            <div>
              <label style={lbl}>To *</label>
              <select value={form.receiver_id} onChange={e => setForm(x => ({...x, receiver_id: e.target.value}))} style={inp}>
                <option value="">Select employee…</option>
                {(employees as any[]).filter((e: any) => e.id !== user?.id).map((e: any) => (
                  <option key={e.id} value={e.id}>{e.name} — {e.designation || e.role}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={lbl}>Message *</label>
              <textarea value={form.message} onChange={e => setForm(x => ({...x, message: e.target.value}))}
                rows={3} placeholder="What would you like to share?" style={{ ...inp, resize: "vertical" }} />
            </div>
            <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 12, color: "#64748b", cursor: "pointer" }}>
              <input type="checkbox" checked={form.is_anonymous} onChange={e => setForm(x => ({...x, is_anonymous: e.target.checked}))} />
              Send anonymously (recipient won't see your name)
            </label>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => giveMut.mutate({ ...form, receiver_id: Number(form.receiver_id) })}
                disabled={!form.receiver_id || !form.message.trim() || giveMut.isPending}
                style={primaryBtn}>{giveMut.isPending ? "Sending…" : "Send Feedback"}</button>
              <button onClick={() => setShowForm(false)} style={{ ...outlineBtn, borderColor: "#e2e8f0", color: "#64748b" }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {items.length === 0 ? (
        <div style={{ textAlign: "center", padding: "24px 0", color: "#94a3b8", fontSize: 13 }}>
          {activeView === "received" ? "No feedback received yet." : "You haven't given any feedback yet."}
        </div>
      ) : (
        <div style={{ display: "grid", gap: 8 }}>
          {items.map((fb: any) => (
            <div key={fb.id} style={{ background: "#f8fafc", borderRadius: 10, padding: "12px 16px", borderLeft: `3px solid ${TEAL}`, minWidth: 0 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: NAVY }}>
                  {activeView === "received" ? `From: ${fb.giver_name}` : `To: ${fb.receiver_name}`}
                </span>
                <span style={{ fontSize: 11, color: "#94a3b8" }}>{fb.created_at?.slice(0, 10)}</span>
              </div>
              <div style={{ fontSize: 13, color: "#374151", wordBreak: "break-word" }}>{fb.message}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Check-ins Tab ──────────────────────────────────────────────────────────────

function CheckInsTab({ employees }: { employees: any[] }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const confirm = useConfirm();
  const { user } = useAuth();
  const isManager = ["admin", "admin_assistant", "hr", "employee"].includes(user?.role || "");
  const [showForm, setShowForm] = useState(false);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [editingNotes, setEditingNotes] = useState<Record<number, string>>({});
  const BLANK = { employee_id: "", title: "", scheduled_at: "", notes: "" };
  const [form, setForm] = useState(BLANK);

  const { data: checkins = [] } = useQuery({ queryKey: ["checkins"], queryFn: P.getCheckins });

  const createMut = useMutation({
    mutationFn: P.createCheckin,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["checkins"] }); setShowForm(false); setForm(BLANK); toast("Check-in created"); },
    onError: (e: any) => toast(getErrorMessage(e, "Failed"), "error"),
  });
  const updateMut = useMutation({
    mutationFn: ({ id, d }: any) => P.updateCheckin(id, d),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["checkins"] }); toast("Saved"); },
    onError: () => toast("Failed to save", "error"),
  });
  const deleteMut = useMutation({
    mutationFn: P.deleteCheckin,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["checkins"] }); toast("Deleted"); },
  });

  const upcoming = (checkins as any[]).filter((c: any) => c.status === "scheduled");
  const completed = (checkins as any[]).filter((c: any) => c.status === "completed");

  const toggleActionItem = (ci: any, idx: number) => {
    const items = [...(ci.action_items || [])];
    items[idx] = { ...items[idx], done: !items[idx].done };
    updateMut.mutate({ id: ci.id, d: { action_items: items } });
  };

  const renderCheckin = (ci: any) => {
    const isOwner = ci.manager_id === user?.id;
    const expanded = expandedId === ci.id;
    const doneCount = (ci.action_items || []).filter((a: any) => a.done).length;
    return (
      <div key={ci.id} style={{ ...card, marginBottom: 10, borderLeft: `4px solid ${ci.status === "completed" ? "#10b981" : NAVY}` }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", cursor: "pointer" }}
          onClick={() => setExpandedId(expanded ? null : ci.id)}>
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 3 }}>
              <span style={{ fontWeight: 700, fontSize: 14, color: "#1e293b" }}>{ci.title}</span>
              <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 10, fontWeight: 600,
                background: ci.status === "completed" ? "#dcfce7" : "#eff6ff",
                color: ci.status === "completed" ? "#166534" : "#1d4ed8" }}>
                {ci.status}
              </span>
            </div>
            <div style={{ fontSize: 12, color: "#64748b" }}>
              {ci.manager_id === user?.id ? `With: ${ci.employee_name}` : `Manager: ${ci.manager_name}`}
              {ci.scheduled_at ? ` · ${ci.scheduled_at}` : ""}
              {ci.action_items?.length > 0 ? ` · ${doneCount}/${ci.action_items.length} actions done` : ""}
            </div>
          </div>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <span style={{ fontSize: 18, color: "#94a3b8" }}>{expanded ? "▲" : "▼"}</span>
            {isOwner && (
              <button onClick={async e => {
                  e.stopPropagation();
                  if (await confirm(`Delete the check-in "${ci.title}"? Its notes and action items go with it.`,
                    { title: "Delete check-in", confirmLabel: "Delete" })) deleteMut.mutate(ci.id);
                }}
                disabled={deleteMut.isPending}
                style={{ background: "none", border: "none", color: "#dc2626", cursor: "pointer", fontSize: 16, padding: "0 4px" }}>×</button>
            )}
          </div>
        </div>

        {expanded && (
          <div style={{ marginTop: 14, borderTop: "1px solid #f1f5f9", paddingTop: 14 }}>
            <div style={{ marginBottom: 14 }}>
              <label style={lbl}>Notes</label>
              {isOwner ? (
                <div style={{ display: "flex", gap: 8 }}>
                  <textarea
                    value={editingNotes[ci.id] ?? (ci.notes || "")}
                    onChange={e => setEditingNotes(x => ({...x, [ci.id]: e.target.value}))}
                    rows={3} style={{ ...inp, flex: 1, resize: "vertical" }}
                    placeholder="Meeting notes, discussion points…" />
                  <button onClick={() => { updateMut.mutate({ id: ci.id, d: { notes: editingNotes[ci.id] ?? ci.notes } }); setEditingNotes(x => { const n = {...x}; delete n[ci.id]; return n; }); }}
                    style={{ ...primaryBtn, alignSelf: "flex-start", fontSize: 12 }}>Save</button>
                </div>
              ) : (
                <div style={{ fontSize: 13, color: "#374151", background: "#f8fafc", borderRadius: 8, padding: 10 }}>
                  {ci.notes || <span style={{ color: "#94a3b8" }}>No notes yet.</span>}
                </div>
              )}
            </div>

            <div>
              <label style={lbl}>Action Items</label>
              <div style={{ display: "grid", gap: 6 }}>
                {(ci.action_items || []).map((a: any, i: number) => (
                  <label key={i} style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13, cursor: "pointer" }}>
                    <input type="checkbox" checked={a.done} onChange={() => toggleActionItem(ci, i)} />
                    <span style={{ textDecoration: a.done ? "line-through" : "none", color: a.done ? "#94a3b8" : "#374151" }}>{a.text}</span>
                  </label>
                ))}
                {isOwner && (
                  <AddActionItem onAdd={(text: string) => {
                    const items = [...(ci.action_items || []), { text, done: false }];
                    updateMut.mutate({ id: ci.id, d: { action_items: items } });
                  }} />
                )}
              </div>
            </div>

            {isOwner && ci.status === "scheduled" && (
              <button onClick={() => updateMut.mutate({ id: ci.id, d: { status: "completed" } })}
                style={{ ...primaryBtn, marginTop: 12, fontSize: 12 }}>Mark Complete</button>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <LoadingOverlay active={createMut.isPending || updateMut.isPending || deleteMut.isPending} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <span style={{ fontSize: 13, color: "#64748b" }}>{(checkins as any[]).length} check-ins total</span>
        <button onClick={() => setShowForm(s => !s)} style={primaryBtn}>+ New Check-in</button>
      </div>

      {showForm && (
        <div style={{ ...card, border: "1.5px solid #bfdbfe", marginBottom: 16 }}>
          <div style={{ display: "grid", gap: 12 }}>
            <div>
              <label style={lbl}>Employee *</label>
              <select value={form.employee_id} onChange={e => setForm(x => ({...x, employee_id: e.target.value}))} style={inp}>
                <option value="">Select employee…</option>
                {(employees as any[]).filter((e: any) => e.id !== user?.id).map((e: any) => (
                  <option key={e.id} value={e.id}>{e.name} — {e.designation || e.role}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={lbl}>Title *</label>
              <input value={form.title} onChange={e => setForm(x => ({...x, title: e.target.value}))}
                placeholder="e.g. Weekly 1:1 — Q2 Goals" style={inp} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <label style={lbl}>Scheduled Date</label>
                <DateInput value={form.scheduled_at} onChange={e => setForm(x => ({...x, scheduled_at: e.target.value}))} style={inp} />
              </div>
            </div>
            <div>
              <label style={lbl}>Notes</label>
              <textarea value={form.notes} onChange={e => setForm(x => ({...x, notes: e.target.value}))}
                rows={3} placeholder="Agenda, talking points…" style={{ ...inp, resize: "vertical" }} />
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => createMut.mutate({ ...form, employee_id: Number(form.employee_id) })}
                disabled={!form.employee_id || !form.title || createMut.isPending}
                style={primaryBtn}>{createMut.isPending ? "Creating…" : "Create Check-in"}</button>
              <button onClick={() => setShowForm(false)} style={{ ...outlineBtn, borderColor: "#e2e8f0", color: "#64748b" }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {upcoming.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <h4 style={{ margin: "0 0 10px", fontSize: 13, fontWeight: 700, color: "#475569" }}>Upcoming ({upcoming.length})</h4>
          {upcoming.map(renderCheckin)}
        </div>
      )}
      {completed.length > 0 && (
        <div>
          <h4 style={{ margin: "0 0 10px", fontSize: 13, fontWeight: 700, color: "#475569" }}>Completed ({completed.length})</h4>
          {completed.map(renderCheckin)}
        </div>
      )}
      {(checkins as any[]).length === 0 && !showForm && (
        <div style={{ textAlign: "center", padding: "60px 0", color: "#94a3b8" }}>
          <div style={{ fontSize: 40 }}>🤝</div>
          <div style={{ marginTop: 10, fontSize: 14 }}>No check-ins yet. Create your first 1:1 to start tracking conversations.</div>
        </div>
      )}
    </div>
  );
}

function AddActionItem({ onAdd }: { onAdd: (text: string) => void }) {
  const [text, setText] = useState("");
  return (
    <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
      <input value={text} onChange={e => setText(e.target.value)}
        onKeyDown={e => { if (e.key === "Enter" && text.trim()) { onAdd(text.trim()); setText(""); } }}
        placeholder="Add action item… (press Enter)" style={{ ...inp, fontSize: 12 }} />
      <button onClick={() => { if (text.trim()) { onAdd(text.trim()); setText(""); } }}
        disabled={!text.trim()}
        style={{ ...primaryBtn, fontSize: 12, whiteSpace: "nowrap" }}>Add</button>
    </div>
  );
}

// ── 9-Box Grid ────────────────────────────────────────────────────────────────

const NINE_BOX_LABELS: Record<string, Record<string, { label: string; bg: string; color: string }>> = {
  high: {
    low:    { label: "Inconsistent Player",  bg: "#fef3c7", color: "#92400e" },
    medium: { label: "High Potential",        bg: "#dbeafe", color: "#1e40af" },
    high:   { label: "Star",                  bg: "#dcfce7", color: "#14532d" },
  },
  medium: {
    low:    { label: "Low Performer",         bg: "#fee2e2", color: "#991b1b" },
    medium: { label: "Core Player",           bg: "#f1f5f9", color: "#334155" },
    high:   { label: "High Performer",        bg: "#ede9fe", color: "#4c1d95" },
  },
  low: {
    low:    { label: "Risk",                  bg: "#fee2e2", color: "#991b1b" },
    medium: { label: "Needs Development",     bg: "#fff7ed", color: "#9a3412" },
    high:   { label: "Rough Diamond",         bg: "#fdf4ff", color: "#701a75" },
  },
};

function NineBoxGrid({ data }: { data: any[] }) {
  const [hoveredCell, setHoveredCell] = useState<string | null>(null);
  if (!data || data.length === 0) return (
    <div style={{ textAlign: "center", padding: "40px 0", color: "#94a3b8", fontSize: 13 }}>
      No completed reviews with promotion recommendations yet.
    </div>
  );

  const potentials = ["high", "medium", "low"] as const;
  const perfs = ["low", "medium", "high"] as const;

  return (
    <div>
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 16 }}>
        <div style={{ fontSize: 11, color: "#64748b", fontWeight: 600 }}>POTENTIAL ↑</div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "60px 1fr 1fr 1fr", gap: 6 }}>
        {/* Y-axis labels + rows */}
        {potentials.map(pot => (
          <React.Fragment key={pot}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", paddingRight: 8, fontSize: 11, fontWeight: 600, color: "#64748b" }}>
              {pot.charAt(0).toUpperCase() + pot.slice(1)}
            </div>
            {perfs.map(perf => {
              const cellKey = `${pot}-${perf}`;
              const meta = NINE_BOX_LABELS[perf][pot];
              const people = data.filter(d => d.performance === perf && d.potential === pot);
              const isHovered = hoveredCell === cellKey;
              return (
                <div key={cellKey}
                  onMouseEnter={() => setHoveredCell(cellKey)}
                  onMouseLeave={() => setHoveredCell(null)}
                  style={{ background: meta.bg, borderRadius: 10, padding: 10, minHeight: 90, position: "relative",
                    border: `2px solid ${isHovered ? meta.color : "transparent"}`, transition: "border 0.15s" }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: meta.color, marginBottom: 6 }}>{meta.label}</div>
                  {people.length === 0 ? (
                    <div style={{ fontSize: 10, color: "#94a3b8" }}>—</div>
                  ) : (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                      {people.map((p, i) => (
                        <span key={i} title={`${p.name} (${p.rating})`}
                          style={{ fontSize: 10, fontWeight: 600, background: "white", borderRadius: 12,
                            padding: "2px 7px", color: meta.color, boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }}>
                          {p.name.split(" ")[0]} {p.rating}
                        </span>
                      ))}
                    </div>
                  )}
                  <div style={{ position: "absolute", bottom: 6, right: 8, fontSize: 16, fontWeight: 800, color: meta.color, opacity: 0.3 }}>
                    {people.length || ""}
                  </div>
                </div>
              );
            })}
          </React.Fragment>
        ))}
        {/* X-axis labels */}
        <div />
        {perfs.map(p => (
          <div key={p} style={{ textAlign: "center", fontSize: 11, fontWeight: 600, color: "#64748b", paddingTop: 4 }}>
            {p.charAt(0).toUpperCase() + p.slice(1)}
          </div>
        ))}
      </div>
      <div style={{ textAlign: "center", fontSize: 11, color: "#64748b", fontWeight: 600, marginTop: 4 }}>PERFORMANCE →</div>
      <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 12 }}>
        Performance = final rating (Low &lt;2.5, Medium 2.5–3.5, High &gt;3.5) · Potential = promotion recommendation
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

type Tab = "my" | "goals" | "checkins" | "cycles" | "analytics";

export default function PerformancePage() {
  const { user } = useAuth();
  const isAdmin = ["admin", "admin_assistant", "hr"].includes(user?.role || "");
  // Tab lives in the URL — the module sidebar is the switcher (spec
  // module-subpage-navs). "/performance" keeps rendering My Reviews, its
  // long-standing default, so existing bookmarks are unaffected.
  const [tab] = useRouteTab<Tab>(
    "/performance",
    { my: "", goals: "goals", checkins: "check-ins", cycles: "cycles", analytics: "analytics" },
    "my",
  );

  const { data: toAction = [] } = useQuery({ queryKey: ["reviews-to-action"], queryFn: P.getToAction });
  const { data: employees = [] } = useQuery({ queryKey: ["employees"], queryFn: () => api.get("/api/employees/").then(r => r.data) });
  const { data: myGoals = [] } = useQuery({ queryKey: ["my-goals"], queryFn: P.getMyGoals });

  const pendingReviews = (toAction as any[]).filter((r: any) => r.status === "self_done").length;
  const ackNeeded = (toAction as any[]).filter((r: any) => r.status === "completed" && r.employee_id === user?.id).length;
  const actionCount = pendingReviews + ackNeeded;
  const pendingGoals = (myGoals as any[]).filter((g: any) => g.status === "pending" || g.status === "in_progress").length;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title" style={{ margin: 0, color: "#0f172a", fontSize: 24, fontWeight: 700 }}>Performance</h1>
          <p style={{ margin: "4px 0 0", color: "#64748b", fontSize: 13 }}>Review cycles, goals, self-assessments, and manager feedback</p>
        </div>
      </div>

      {/* Tab pill bar removed — the module sidebar is the switcher now. The
          pending-count chips the pills carried surface here instead so the
          signal survives the move. */}
      {(actionCount > 0 && tab !== "my") || (pendingGoals > 0 && tab !== "goals") ? (
        <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
          {actionCount > 0 && tab !== "my" && (
            <Link to="/performance" style={{ fontSize: 12.5, fontWeight: 600, color: "#b91c1c", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 999, padding: "4px 12px", textDecoration: "none" }}>
              {actionCount} review{actionCount !== 1 ? "s" : ""} awaiting your action →
            </Link>
          )}
          {pendingGoals > 0 && tab !== "goals" && (
            <Link to="/performance/goals" style={{ fontSize: 12.5, fontWeight: 600, color: "#1d4ed8", background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 999, padding: "4px 12px", textDecoration: "none" }}>
              {pendingGoals} open goal{pendingGoals !== 1 ? "s" : ""} →
            </Link>
          )}
        </div>
      ) : null}

      {tab === "my"        && <MyReviewsTab employees={employees as any[]} />}
      {tab === "goals"     && <GoalsTab employees={employees as any[]} isAdmin={isAdmin} />}
      {tab === "checkins"  && <CheckInsTab employees={employees as any[]} />}
      {tab === "cycles"    && isAdmin && <CyclesTab employees={employees as any[]} />}
      {tab === "analytics" && isAdmin && <AnalyticsTab />}
    </div>
  );
}
